import { Crosshair, LocateFixed, MapPin, Route, Undo2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, api, type AddableKind, type CableTier, type LngLat } from '../api'
import { useApp } from '../context'
import { useNarrow } from '../hooks'
import { NODE_KIND_LABEL, TIER_LABEL, fmtM } from '../format'
import { lineLengthM, haversineM } from '../../../src/domain/geo'
import { nextInSeries } from '../../../src/domain/assets'
import { PanelShell } from './PanelShell'
import { Button, Callout, Section, cx } from './ui'

const input = 'h-10 w-full rounded-lg border border-border bg-surface px-2 text-sm'
const label = 'mb-1 block text-sm font-medium'
const DEFAULT_RATIO: Partial<Record<AddableKind, number>> = { nap: 8, lcp: 8 }
const DEFAULT_FIBRES: Record<CableTier, number> = { backbone: 96, feeder: 48, distribution: 12, drop: 2 }

function Field({ id, text, hint, error, children }: { id: string; text: string; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className={label}>
        {text}
      </label>
      {children}
      {(error || hint) && (
        <p id={`${id}-hint`} className={`mt-1 text-xs ${error ? 'text-danger' : 'text-muted'}`}>
          {error || hint}
        </p>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ add a site

export function AddNodePanel() {
  const { edit, setEdit, network, refreshNetwork, replace, back, notify, focusOn } = useApp()
  const narrow = useNarrow()
  const place = edit.kind === 'place' ? edit : null
  const [added, setAdded] = useState(0)
  const [kind, setKind] = useState<AddableKind>(place?.nodeKind ?? 'nap')
  const [name, setName] = useState('')
  const [status, setStatus] = useState<'active' | 'planned'>('active')
  const [ratio, setRatio] = useState<number>(DEFAULT_RATIO[kind] ?? 8)
  const [locality, setLocality] = useState('')
  const [address, setAddress] = useState('')
  const [busy, setBusy] = useState(false)
  const [gps, setGps] = useState<string | null>(null)
  const [accuracyM, setAccuracyM] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ratios, setRatios] = useState<number[]>([2, 4, 8, 16, 32, 64])
  const location = place?.location
  const kindRef = useRef(kind)
  kindRef.current = kind

  // Suggested name for the kind, and the locality of the nearest existing site.
  useEffect(() => {
    api.assetOptions(kind, 'distribution').then((o) => {
      setName((n) => (n === '' || /^(NAP|LCP|CL|POLE|HH)-\d{4}$/.test(n) ? o.suggestedName : n))
      setRatios(o.splitterRatios)
    }, () => undefined)
    setRatio(DEFAULT_RATIO[kind] ?? 8)
  }, [kind])
  useEffect(() => {
    if (!location || locality) return
    let best: { d: number; l?: string } = { d: Infinity }
    for (const n of network.nodes) {
      if (!n.locality) continue
      const d = haversineM(location, n.location)
      if (d < best.d) best = { d, l: n.locality }
    }
    if (best.l && best.d < 1500) setLocality(best.l)
  }, [location, locality, network.nodes])

  const cancel = () => {
    setEdit({ kind: 'none' })
    back()
  }
  const pickOnMap = () => setEdit({ kind: 'place', nodeKind: kind, location, picking: true })
  const useGps = useCallback(() => {
    if (!navigator.geolocation) return setGps('This browser cannot read GPS.')
    setGps('Reading GPS…')
    setAccuracyM(null)
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const at: LngLat = [p.coords.longitude, p.coords.latitude]
        setEdit({ kind: 'place', nodeKind: kindRef.current, location: at, picking: false })
        setAccuracyM(p.coords.accuracy)
        setGps(null)
        focusOn(at, 19)
      },
      (e) => setGps(e.code === e.PERMISSION_DENIED ? 'Location permission denied. Tap the map where the box is.' : 'No GPS fix yet. Tap the map where the box is, or try again.'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOn, setEdit])

  // On a phone the technician is standing at the box: take the GPS fix straight away, so nobody
  // types coordinates. They can still move the pin, and a denied permission falls back to tapping.
  const gpsTried = useRef(false)
  useEffect(() => {
    if (!narrow || gpsTried.current || location) return
    gpsTried.current = true
    useGps()
  }, [narrow, location, useGps])

  /** Save; with `again`, stay in adding mode for the next site of the same kind (next name in the series). */
  const save = async (e: React.FormEvent | React.MouseEvent, again = false) => {
    e.preventDefault()
    if (!location) return setError('Place the site on the map or use GPS first.')
    setBusy(true)
    setError(null)
    try {
      const { node } = await api.createNode({
        kind,
        name: name.trim(),
        location,
        status,
        locality: locality.trim() || undefined,
        address: address.trim() || undefined,
        splitterRatio: kind === 'nap' || kind === 'lcp' ? ratio : undefined,
      })
      refreshNetwork()
      notify(`Added ${kind.toUpperCase()} ${node.name}`, {
        label: 'Undo',
        run: async () => {
          try {
            await api.deleteNode(node.id, 1)
            refreshNetwork()
            notify(`Removed ${node.name}`)
          } catch (err) {
            notify(err instanceof Error ? `Not undone: ${err.message}` : 'Not undone')
          }
        },
      })
      if (again) {
        // Same kind, splitter, status and locality; next name; tap the map again.
        setAdded((n) => n + 1)
        setName(nextInSeries(node.name, [...network.nodes.map((n) => n.name), node.name]))
        setAddress('')
        setGps(null)
        setAccuracyM(null)
        gpsTried.current = false // the next box is a few poles away: read GPS there too
        setEdit({ kind: 'place', nodeKind: kind, picking: true })
      } else {
        setEdit({ kind: 'none' })
        replace({ kind: 'node', id: node.id })
      }
    } catch (err) {
      setError(err instanceof ApiError && err.issues?.length ? err.issues.join('; ') : err instanceof Error ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <PanelShell
      title={`Add ${NODE_KIND_LABEL[kind].split(' (')[0]}`}
      subtitle={added ? `${added} added in this run · saved with your name in the audit log` : 'Saved with your name in the audit log.'}
      onClose={cancel}
    >
      <Section title="Where">
        {location ? (
          <div>
            <p className="num text-sm" data-testid="add-coords">
              <MapPin className="mr-1 inline h-4 w-4 text-accent" aria-hidden />
              {location[1].toFixed(6)}, {location[0].toFixed(6)}
            </p>
            {accuracyM !== null && (
              <p className={cx('mt-1 text-xs', accuracyM > 25 ? 'text-warn' : 'text-muted')} aria-live="polite">
                {accuracyM > 25
                  ? `GPS accurate to about ±${Math.round(accuracyM)} m — stand still for a better fix, or move the pin on the map.`
                  : `From your phone's GPS, ±${Math.round(accuracyM)} m.`}
              </p>
            )}
          </div>
        ) : (
          <Callout tone="accent" title={gps === 'Reading GPS…' ? 'Getting your location…' : 'Tap the map where it is, or use GPS if you are standing at it.'} />
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" type="button" onClick={pickOnMap}>
            <Crosshair className="h-4 w-4" aria-hidden /> {location ? 'Move pin on map' : 'Place on map'}
          </Button>
          <Button size="sm" type="button" onClick={useGps} busy={gps === 'Reading GPS…'}>
            <LocateFixed className="h-4 w-4" aria-hidden /> {location && accuracyM !== null ? 'Read GPS again' : 'Use my GPS location'}
          </Button>
        </div>
        {gps && gps !== 'Reading GPS…' && (
          <p className="mt-1 text-xs text-muted" aria-live="polite">
            {gps}
          </p>
        )}
      </Section>
      <Section title="Details">
        <form className="space-y-3" onSubmit={save}>
          {error && <Callout tone="danger" title="Not saved">{error}</Callout>}
          <Field id="add-kind" text="Type">
            <select id="add-kind" className={input} value={kind} onChange={(e) => setKind(e.target.value as AddableKind)}>
              {(['nap', 'lcp', 'closure', 'pole', 'handhole'] as const).map((k) => (
                <option key={k} value={k}>
                  {NODE_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </Field>
          <Field id="add-name" text="Name" hint="Must be unique. Use your naming standard, e.g. NAP-0123.">
            <input id="add-name" className={input} value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={64} autoComplete="off" />
          </Field>
          {(kind === 'nap' || kind === 'lcp') && (
            <Field id="add-ratio" text="Splitter" hint={kind === 'nap' ? 'Subscriber ports on this NAP.' : 'Primary split at this LCP.'}>
              <select id="add-ratio" className={input} value={ratio} onChange={(e) => setRatio(Number(e.target.value))}>
                {ratios.map((r) => (
                  <option key={r} value={r}>
                    1:{r}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <fieldset>
            <legend className={label}>Status</legend>
            <div className="flex gap-4 text-sm">
              {(['active', 'planned'] as const).map((s) => (
                <label key={s} className="flex items-center gap-1.5">
                  <input type="radio" name="add-status" checked={status === s} onChange={() => setStatus(s)} /> {s === 'active' ? 'Installed (active)' : 'Planned'}
                </label>
              ))}
            </div>
          </fieldset>
          <Field id="add-locality" text="Barangay / municipality" hint="Customers see this in outage advisories.">
            <input id="add-locality" className={input} value={locality} onChange={(e) => setLocality(e.target.value)} maxLength={120} placeholder="e.g. Burgos, Rodriguez, Rizal" />
          </Field>
          <Field id="add-address" text="Address or landmark (optional)">
            <input id="add-address" className={input} value={address} onChange={(e) => setAddress(e.target.value)} maxLength={200} placeholder="e.g. Blk 12 corner Road 4, near the chapel" />
          </Field>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="submit" variant="primary" busy={busy} disabled={!location || name.trim().length < 2}>
              Save {kind.toUpperCase()}
            </Button>
            <Button type="button" busy={busy} disabled={!location || name.trim().length < 2} onClick={(e) => save(e, true)} title="Save, then place the next one with the next name">
              Save &amp; add another
            </Button>
            <Button type="button" variant="ghost" onClick={cancel}>
              {added ? 'Done' : 'Cancel'}
            </Button>
          </div>
        </form>
      </Section>
    </PanelShell>
  )
}

// ------------------------------------------------------------------ add a cable

export function AddCablePanel() {
  const { edit, setEdit, network, refreshNetwork, replace, back, notify } = useApp()
  const draft = edit.kind === 'cable' ? edit : null
  const [added, setAdded] = useState(0)
  const tierTouched = useRef(false)
  const from = network.nodes.find((n) => n.id === draft?.fromNodeId)
  const to = network.nodes.find((n) => n.id === draft?.toNodeId)
  const defaultTier: CableTier = from?.kind === 'nap' || to?.kind === 'nap' ? 'distribution' : 'feeder'
  const [tier, setTier] = useState<CableTier>(defaultTier)
  const [fibres, setFibres] = useState(DEFAULT_FIBRES[defaultTier])
  const [name, setName] = useState('')
  const [status, setStatus] = useState<'active' | 'planned'>('active')
  const [measured, setMeasured] = useState('')
  const [counts, setCounts] = useState<number[]>([2, 4, 6, 8, 12, 24, 48, 72, 96, 144, 288])
  const [streets, setStreets] = useState(false)
  const [busy, setBusy] = useState<'save' | 'route' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [detour, setDetour] = useState<{ lengthM: number; straightM: number } | null>(null)

  // Default tier/fibres once both ends are known: NAP involved -> distribution 12F, else feeder 48F.
  useEffect(() => {
    if (!from || !to || tierTouched.current) return // keep the user's choice when chaining cables
    setTier(defaultTier)
    setFibres(DEFAULT_FIBRES[defaultTier])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from?.id, to?.id])

  useEffect(() => {
    api.assetOptions('nap', tier).then((o) => {
      setName((n) => (n === '' || /^(BB|FDR|DST|DRP)-\d{4}$/.test(n) ? o.suggestedCableName : n))
      setCounts(o.fiberCounts)
      setStreets(o.streetRouting)
    }, () => undefined)
  }, [tier])

  const line = useMemo<LngLat[]>(() => (from && to && draft ? [from.location, ...draft.via, to.location] : []), [from, to, draft])
  const lengthM = line.length > 1 ? lineLengthM(line) : 0
  const measuredNum = measured === '' ? undefined : Number(measured)
  const measuredError = measuredNum !== undefined && !(measuredNum > 0 && measuredNum < 100000) ? 'Enter metres from the cable markings' : ''

  const cancel = () => {
    setEdit({ kind: 'none' })
    back()
  }
  if (!draft || !from || !to) {
    return (
      <PanelShell title="Add cable" subtitle={added ? `${added} added in this run` : undefined} onClose={cancel}>
        <div className="space-y-3 p-4">
          <Callout tone="accent" title={from ? `From ${from.name}` : 'Tap the START site on the map'}>
            {from ? 'Tap bend points along the route, then tap the END site. Undo a point from the bar on the map.' : 'Then tap bend points along the route, and finally the END site.'}
          </Callout>
          <ol className="space-y-1 text-sm">
            <li className={from ? 'text-muted line-through' : 'font-medium'}>1. Start site{from ? `: ${from.name}` : ''}</li>
            <li className={from ? 'font-medium' : 'text-muted'}>2. Bend points: {draft?.via.length ?? 0}</li>
            <li className="text-muted">3. End site</li>
          </ol>
          <Button onClick={cancel}>{added ? 'Done' : 'Cancel'}</Button>
        </div>
      </PanelShell>
    )
  }

  const followStreets = async () => {
    setBusy('route')
    setError(null)
    try {
      const r = await api.streetRoute(from.id, to.id, tier)
      setEdit({ ...draft, via: r.via })
      setDetour(r.detour ? { lengthM: r.lengthM, straightM: r.straightM } : null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No street route')
    } finally {
      setBusy(null)
    }
  }
  const redraw = () => {
    setDetour(null)
    setEdit({ kind: 'cable', fromNodeId: from.id, via: [] })
  }

  /** Save; with `cont`, start the next cable from this one's end (chaining NAPs along a street). */
  const save = async (e: React.FormEvent | React.MouseEvent, cont = false) => {
    e.preventDefault()
    setBusy('save')
    setError(null)
    try {
      const { cable } = await api.createCable({ name: name.trim(), tier, fromNodeId: from.id, toNodeId: to.id, fiberCount: fibres, status, via: draft.via, measuredLengthM: measuredNum })
      refreshNetwork()
      notify(`Added cable ${cable.name}`, {
        label: 'Undo',
        run: async () => {
          try {
            await api.deleteCable(cable.id)
            refreshNetwork()
            notify(`Removed ${cable.name}`)
          } catch (err) {
            notify(err instanceof Error ? `Not undone: ${err.message}` : 'Not undone')
          }
        },
      })
      if (cont) {
        setAdded((n) => n + 1)
        setName(nextInSeries(cable.name, [...network.cables.map((c) => c.name), cable.name]))
        setMeasured('')
        setDetour(null)
        setEdit({ kind: 'cable', fromNodeId: to.id, via: [] })
      } else {
        setEdit({ kind: 'none' })
        replace({ kind: 'cable', id: cable.id })
      }
    } catch (err) {
      setError(err instanceof ApiError && err.issues?.length ? err.issues.join('; ') : err instanceof Error ? err.message : 'Could not save')
    } finally {
      setBusy(null)
    }
  }

  return (
    <PanelShell title="Add cable" subtitle={`${from.name} → ${to.name}`} onClose={cancel}>
      <Section title="Route">
        <p className="text-sm">
          <strong className="num">{fmtM(lengthM)}</strong> along the drawn route · {draft.via.length} bend point{draft.via.length === 1 ? '' : 's'}
        </p>
        {detour && (
          <div className="mt-2">
            <Callout tone="warn" title={`The street route is ${fmtM(detour.lengthM)} for a ${fmtM(detour.straightM)} gap`}>
              The streets probably do not connect here in the map data. Use a straight line, or redraw by tapping the real route.
              <div className="mt-2">
                <Button
                  size="sm"
                  type="button"
                  onClick={() => {
                    setDetour(null)
                    setEdit({ ...draft, via: [] })
                  }}
                >
                  Use straight line
                </Button>
              </div>
            </Callout>
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          {streets && (
            <Button size="sm" type="button" busy={busy === 'route'} onClick={followStreets}>
              <Route className="h-4 w-4" aria-hidden /> Follow streets
            </Button>
          )}
          <Button size="sm" type="button" onClick={redraw}>
            <Undo2 className="h-4 w-4" aria-hidden /> Redraw route
          </Button>
        </div>
      </Section>
      <Section title="Details">
        <form className="space-y-3" onSubmit={save}>
          {error && <Callout tone="danger" title="Not saved">{error}</Callout>}
          <div className="grid grid-cols-2 gap-2">
            <Field id="cab-tier" text="Tier">
              <select
                id="cab-tier"
                className={input}
                value={tier}
                onChange={(e) => {
                  const t = e.target.value as CableTier
                  tierTouched.current = true
                  setTier(t)
                  setFibres(DEFAULT_FIBRES[t])
                }}
              >
                {(Object.keys(TIER_LABEL) as CableTier[]).map((t) => (
                  <option key={t} value={t}>
                    {TIER_LABEL[t]}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="cab-fibres" text="Fibre count">
              <select id="cab-fibres" className={input} value={fibres} onChange={(e) => setFibres(Number(e.target.value))}>
                {counts.map((c) => (
                  <option key={c} value={c}>
                    {c}F
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field id="cab-name" text="Name" hint="Must be unique.">
            <input id="cab-name" className={input} value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={64} autoComplete="off" />
          </Field>
          <Field id="cab-measured" text="Measured length, metres (optional)" hint={measuredError || 'From the sheath markings; used for loss and OTDR instead of the map length.'} error={measuredError}>
            <input id="cab-measured" className={input} inputMode="decimal" value={measured} onChange={(e) => setMeasured(e.target.value)} placeholder={String(Math.round(lengthM))} />
          </Field>
          <fieldset>
            <legend className={label}>Status</legend>
            <div className="flex gap-4 text-sm">
              {(['active', 'planned'] as const).map((s) => (
                <label key={s} className="flex items-center gap-1.5">
                  <input type="radio" name="cab-status" checked={status === s} onChange={() => setStatus(s)} /> {s === 'active' ? 'Installed (active)' : 'Planned'}
                </label>
              ))}
            </div>
          </fieldset>
          <p className="text-xs text-muted">After saving, splice its cores at each end from the site's Splices tab.</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="submit" variant="primary" busy={busy === 'save'} disabled={name.trim().length < 2 || Boolean(measuredError)}>
              Save cable
            </Button>
            <Button type="button" busy={busy === 'save'} disabled={name.trim().length < 2 || Boolean(measuredError)} onClick={(e) => save(e, true)} title={`Save, then draw the next cable starting at ${to.name}`}>
              Save &amp; continue from {to.name}
            </Button>
            <Button type="button" variant="ghost" onClick={cancel}>
              {added ? 'Done' : 'Cancel'}
            </Button>
          </div>
        </form>
      </Section>
    </PanelShell>
  )
}
